/*
 *   File : other-transaction-detail-modal.js
 *   Description : Switch transaction detail modal
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { Icon } from '../Icon';
import { Chip } from '../Others';

import style from '../../Style/Component/tooltip.module.scss';

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") {
        return "-";
    }
    return value;
};

const getStatusTextClass = (status) => {
    const normalizedStatus = status?.toLowerCase();

    if (normalizedStatus === "success" || normalizedStatus === "successful") {
        return "e-text-green-crayola";
    }
    if (normalizedStatus === "failed") return "e-text-maximum-red";
    if (normalizedStatus === "pending") return "e-text-marigold";
    if (normalizedStatus === "submitted" || normalizedStatus === "in progress") return "e-text-bleu-de-france";
    return "e-text-raisin-black";
};

const formatInitiatedDate = (orderDate) => {
    if (!orderDate) return "-";

    const dateMatch = orderDate.match(/^(.*?\d{4})\s+(\d{1,2}:\d{2}\s*(?:AM|PM))$/i);
    return dateMatch ? `${dateMatch[1]} | ${dateMatch[2]}` : orderDate;
};

const DetailRow = ({ label, value, valueClass = "e-text-smoky-black", truncate = false, tooltipId }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const displayText = displayValue(value);
    const showTooltip = truncate && displayText !== "-" && isTruncated;

    useEffect(() => {
        const el = textRef.current;
        if (!el || !truncate || displayText === "-") {
            setIsTruncated(false);
            return;
        }

        const checkTruncation = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        };

        checkTruncation();
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, [displayText, truncate]);

    return (
        <div className='d-flex justify-content-between align-items-start gap-3 py-2 border-bottom'>
            <p className='mb-0 e-text-dove-gray e-primary-font e-font-16 e-font-weight-400 line-height-24px flex-shrink-0'>
                {label}
            </p>
            {truncate ?
                <div className={`d-flex justify-content-end flex-grow-1 e-primary-font e-font-16 e-font-weight-600 line-height-24px ${valueClass}`}
                    style={{ minWidth: 0 }}>
                    <p ref={textRef}
                        className='mb-0 text-truncate'
                        style={{ maxWidth: '100%', width: 'fit-content' }}
                        data-tooltip-id={showTooltip ? tooltipId : undefined}
                        data-tooltip-content={showTooltip ? displayText : undefined}>
                        {displayText}
                    </p>
                    {showTooltip &&
                        <Tooltip id={tooltipId}
                            place="bottom-end"
                            offset={4}
                            positionStrategy="fixed"
                            className={style.e_custom_tooltip}
                            style={{
                                width: 'auto',
                                maxWidth: '320px',
                                padding: '0.5rem 0.75rem',
                            }} />
                    }
                </div>
                :
                <p className={`mb-0 text-end text-break flex-grow-1 e-primary-font e-font-16 e-font-weight-600 line-height-24px ${valueClass}`}
                    style={{ minWidth: 0 }}>
                    {displayText}
                </p>
            }
        </div>
    );
};

const TransactionCard = ({ title, rows }) => (
    <div className='h-100 border-radius-8 p-3' style={{ backgroundColor: "#F5F3FF" }}>
        <p className='mb-2 e-text-dravit-grey e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
            {title}
        </p>

        {rows.map((row) => (
            <DetailRow key={row.label}
                label={row.label}
                value={row.value}
                valueClass={row.valueClass}
                truncate={row.truncate}
                tooltipId={row.tooltipId} />
        ))}
    </div>
);

const OtherTransactionDetailModal = ({ data = {}, handleClose = () => { } }) => {
    const historyPushedRef = useRef(false);
    const ignorePopStateRef = useRef(false);

    const isFailed = data.order_status?.toLowerCase() === "failed"
        || data.fp_order_status?.toLowerCase() === "failed";

    const switchOutRows = [
        { label: "Scheme", value: data.switch_out?.name, truncate: true, tooltipId: "switch-out-scheme" },
        { label: "Folio", value: data.switch_out?.folio || data.folio_no },
        { label: "Amount", value: data.switch_out?.amount },
        { label: "NAV", value: data.switch_out?.nav },
        { label: "NAV date", value: data.switch_out?.date },
        { label: "Switched out units", value: data.switch_out?.units },
        { label: "FP Order ID", value: data.fp_order_id },
        { label: "Exit load", value: data.exit_load },
        {
            label: "Rayi order status",
            value: data.order_status,
            valueClass: getStatusTextClass(data.order_status),
        },
        {
            label: "FP order status",
            value: data.fp_order_status,
            valueClass: getStatusTextClass(data.fp_order_status),
        },
    ];

    const switchInRows = [
        { label: "Scheme", value: data.switch_in?.name, truncate: true, tooltipId: "switch-in-scheme" },
        { label: "Folio", value: data.switch_in?.folio || data.folio_no },
        { label: "Amount", value: data.switch_in?.amount },
        { label: "NAV", value: data.switch_in?.nav },
        { label: "NAV date", value: data.switch_in?.date },
        { label: "Switched in units", value: data.switch_in?.units },
        { label: "FP Order ID", value: data.fp_order_id },
        { label: "Settlement date", value: data.switch_in?.date },
        {
            label: "Rayi order status",
            value: data.order_status,
            valueClass: getStatusTextClass(data.order_status),
        },
        {
            label: "FP order status",
            value: data.fp_order_status,
            valueClass: getStatusTextClass(data.fp_order_status),
        },
    ];

    const _dismissModal = () => {
        document.getElementById("other-transaction-detail-modal-close")?.click();
        document.querySelector(".modal-backdrop")?.remove();
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "unset";
        handleClose();
    };

    const _handleCloseModal = () => {
        if (historyPushedRef.current) {
            ignorePopStateRef.current = true;
            historyPushedRef.current = false;
            _dismissModal();
            window.history.back();
            return;
        }
        _dismissModal();
    };

    useEffect(() => {
        const modalEl = document.getElementById("other-transaction-detail-modal");
        if (!modalEl) return;

        const _handleModalShown = () => {
            if (!historyPushedRef.current) {
                window.history.pushState({ otherTransactionDetailModal: true }, "");
                historyPushedRef.current = true;
            }
        };

        const _handlePopState = () => {
            if (ignorePopStateRef.current) {
                ignorePopStateRef.current = false;
                return;
            }

            if (historyPushedRef.current || modalEl.classList.contains("show")) {
                historyPushedRef.current = false;
                _dismissModal();
            }
        };

        modalEl.addEventListener("shown.bs.modal", _handleModalShown);
        window.addEventListener("popstate", _handlePopState);

        return () => {
            modalEl.removeEventListener("shown.bs.modal", _handleModalShown);
            window.removeEventListener("popstate", _handlePopState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='modal fade'
            id='other-transaction-detail-modal'
            data-bs-backdrop='static'
            data-bs-keyboard='false'
            tabIndex='-1'
            aria-labelledby='otherTransactionDetailModalLabel'
            aria-hidden='true'>
            <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'>
                <span id='other-transaction-detail-modal-close'
                    className='d-none'
                    data-bs-dismiss='modal'>
                </span>

                <div className='modal-content border-0 border-radius-28 p-4'>
                    <div className='modal-header border-0 border-bottom p-0 pb-3 justify-content-between align-items-start'>
                        <div>
                            <h6 id='otherTransactionDetailModalLabel'
                                className='mb-2 e-text-raisin-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                {displayValue(data.name)}
                            </h6>
                            <div className='d-flex align-items-center gap-2'>
                                <p className='mb-0 e-text-davys-grey-light e-primary-font e-font-14 e-font-weight-400 line-height-20px'>
                                    {displayValue(data.name)} ({displayValue(data.customer_display_id)}) | Initiated {formatInitiatedDate(data.order_date)}

                                </p>
                                {data.switch_mode &&
                                    <Chip label={data.switch_mode==="FULL" ? "Full Amount" : "Partial Amount"}
                                        className={data.switch_mode === "FULL" ? "e-bg-green-crayola" : "e-bg-marigold"} />
                                }
                            </div>
                        </div>

                        <Icon icon='close'
                            size='1.125rem'
                            className='d-flex cursor-pointer'
                            onClick={_handleCloseModal} />
                    </div>

                    <div className='modal-body px-0 py-3' style={{ overflowX: "hidden" }}>
                        <div className='row row-cols-1 row-cols-lg-2 g-3 mx-0'>
                            <div className='col'>
                                <TransactionCard title='Switch out (Redeem)' rows={switchOutRows} />
                            </div>
                            <div className='col'>
                                <TransactionCard title='Switch in (Purchase)' rows={switchInRows} />
                            </div>
                        </div>

                        {isFailed &&
                            <div className='p-2 mt-3'
                                style={{
                                    backgroundColor: "#F8C0C1",
                                    border: "1px solid #772D2C",
                                    borderRadius: "8px",
                                    color: "#772D2C",
                                }}>
                                <p className='mb-1 e-primary-font e-font-12 e-font-weight-600 line-height-16px'>
                                    Failed reason
                                </p>
                                <p className='mb-0 e-primary-font e-font-12 e-font-weight-400 line-height-16px'>
                                    {displayValue(data.failure_reason)}
                                </p>
                            </div>
                        }
                    </div>

                    <div className='modal-footer border-0 p-0 justify-content-start'>
                        <p className='mb-0 e-text-dark-silver e-primary-font e-font-12 e-font-weight-400 line-height-16px'>
                            Last updated on {data.order_status_updated_on}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OtherTransactionDetailModal;
